import {
  IQWatchDevice,
  IQWatchDeviceSiteResponse,
  CreateIQWatchDeviceParams,
  UpdateIQWatchDeviceParams,
  IQWatchDeviceListResponse,
  IQWatchDeviceHistoryResponse,
  RouterStatus,
  RouterStatusParams,
  RouterHistoryDataPoint,
  RouterHistoryParams,
} from "../types";
import api from "./api.ts";
import { getIQWatchDevicesBySiteMock } from "../data/iqwatchMockData.ts";

// 开发环境标志 - 您可以根据需要调整这个
const USE_MOCK_DATA = false; // 设置为 true 来使用 mock 数据

// Helper function to map time_zone to timezone in device responses
const mapDeviceResponse = (device: any): IQWatchDevice => {
  if (device.time_zone !== undefined && device.timezone === undefined) {
    device.timezone = device.time_zone;
    delete device.time_zone;
  }
  return device;
};

// Helper function to map time_zone to timezone in device array responses
const mapDeviceArrayResponse = (devices: any[]): any[] => {
  return devices.map((device) => {
    if (device.device) {
      // For IQWatchDeviceWithData structure
      return {
        ...device,
        device: mapDeviceResponse(device.device),
      };
    }
    return mapDeviceResponse(device);
  });
};

// 创建新的 iQWatchDevice
export const createIQWatchDevice = async (
  params: CreateIQWatchDeviceParams
): Promise<IQWatchDevice> => {
  try {
    // Map timezone to time_zone for API compatibility
    const apiParams: any = { ...params };
    if (apiParams.timezone !== undefined) {
      apiParams.time_zone = apiParams.timezone;
      delete apiParams.timezone;
    }
    const response = await api.post("/iqwatch-devices", apiParams);
    // Map time_zone back to timezone in response
    if (response.data.time_zone !== undefined) {
      response.data.timezone = response.data.time_zone;
      delete response.data.time_zone;
    }
    return response.data;
  } catch (error) {
    console.error("iqwatchService: API call failed with error:", error);
    throw error; // Re-throw to maintain the error flow
  }
};

// 删除 iQWatchDevice
export const deleteIQWatchDevice = async (id: string): Promise<void> => {
  await api.delete(`/iqwatch-devices/${id}`);
};

// 更新 iQWatchDevice
export const updateIQWatchDevice = async (
  id: string,
  params: UpdateIQWatchDeviceParams
): Promise<IQWatchDevice> => {
  // Map timezone to time_zone for API compatibility
  const apiParams: any = { ...params };
  if (apiParams.timezone !== undefined) {
    apiParams.time_zone = apiParams.timezone;
    delete apiParams.timezone;
  }
  const response = await api.put(`/iqwatch-devices/${id}`, apiParams);
  // Map time_zone back to timezone in response
  if (response.data.time_zone !== undefined) {
    response.data.timezone = response.data.time_zone;
    delete response.data.time_zone;
  }
  return response.data;
};

// 获取指定站点的 iQWatchDevice 数据 (添加分页、排序、搜索和过滤支持)
export const getIQWatchDevicesBySite = async (
  siteId: string,
  orgId: string,
  page: number = 1,
  limit: number = 10,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  search?: string,
  filter?: "all" | "online" | "offline" | "low_battery"
): Promise<IQWatchDeviceSiteResponse> => {
  if (USE_MOCK_DATA) {
    // 使用 mock 数据
    // 模拟API延迟
    await new Promise((resolve) => setTimeout(resolve, 300));
    const result = getIQWatchDevicesBySiteMock(siteId, orgId, page, limit);
    return result;
  }

  // 真实API调用
  const params = new URLSearchParams({
    orgId,
    page: page.toString(),
    limit: limit.toString(),
  });

  if (sortBy) {
    params.append("sortBy", sortBy);
  }
  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }
  if (search && search.trim()) {
    params.append("search", search.trim());
  }
  if (filter && filter !== "all") {
    params.append("filter", filter);
  }

  const response = await api.get(
    `/iqwatch-devices/site/${siteId}/latest?${params.toString()}`
  );
  // Map time_zone to timezone in response
  if (response.data.devices) {
    response.data.devices = mapDeviceArrayResponse(response.data.devices);
  }
  return response.data;
};

// 获取分页的 iQWatchDevice 列表 (添加排序、搜索和过滤支持)
export const getIQWatchDevicesList = async (
  orgId: string,
  page: number = 1,
  limit: number = 20,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  search?: string,
  filter?: "all" | "online" | "offline" | "low_battery"
): Promise<IQWatchDeviceListResponse> => {
  const params = new URLSearchParams({
    orgId,
    page: page.toString(),
    limit: limit.toString(),
  });

  if (sortBy) {
    params.append("sortBy", sortBy);
  }
  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }
  if (search && search.trim()) {
    params.append("search", search.trim());
  }
  if (filter && filter !== "all") {
    params.append("filter", filter);
  }

  const response = await api.get(`/iqwatch-devices/list?${params.toString()}`);
  // Map time_zone to timezone in response
  if (response.data.devices) {
    response.data.devices = response.data.devices.map(mapDeviceResponse);
  }
  return response.data;
};

// 获取所有组织的 iQWatchDevice 列表 (添加排序、搜索和过滤支持)
export const getAllIQWatchDevices = async (
  page: number = 1,
  limit: number = 20,
  sortBy?: string,
  sortOrder?: "asc" | "desc",
  search?: string,
  filter?: "all" | "online" | "offline" | "low_battery"
): Promise<IQWatchDeviceListResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString(),
  });

  if (sortBy) {
    params.append("sortBy", sortBy);
  }
  if (sortOrder) {
    params.append("sortOrder", sortOrder);
  }
  if (search && search.trim()) {
    params.append("search", search.trim());
  }
  if (filter && filter !== "all") {
    params.append("filter", filter);
  }

  const response = await api.get(`/iqwatch-devices/all?${params.toString()}`);
  // Map time_zone to timezone in response
  if (response.data.devices) {
    response.data.devices = response.data.devices.map(mapDeviceResponse);
  }
  return response.data;
};

// 获取 iQWatchDevice 历史数据
export const getIQWatchDeviceHistory = async (
  deviceId: string,
  range: string = "24h"
): Promise<IQWatchDeviceHistoryResponse> => {
  const response = await api.get(
    `/iqwatch-devices/${deviceId}/history?range=${range}`
  );
  // Map time_zone to timezone in response
  if (response.data.device) {
    response.data.device = mapDeviceResponse(response.data.device);
  }
  return response.data;
};

// 同步所有设备的最新状态
export const syncAllDevices = async (
  orgId: string
): Promise<{
  total: number;
  success: number;
  failed: number;
  results: Array<{
    deviceId: string;
    deviceName: string;
    status: string;
  }>;
}> => {
  const response = await api.post("/iqwatch-devices/sync-all", { orgId });
  return response.data;
};

// 获取路由器状态
export const getRouterStatus = async (
  params: RouterStatusParams
): Promise<RouterStatus> => {
  const queryParams = new URLSearchParams();

  if (params.serial) {
    queryParams.append("serial", params.serial);
  }
  if (params.mac) {
    queryParams.append("mac", params.mac);
  }
  if (params.imei) {
    queryParams.append("imei", params.imei);
  }

  // At least one parameter must be provided
  if (queryParams.toString() === "") {
    throw new Error("At least one of serial, mac, or imei must be provided");
  }

  const response = await api.get(
    `/iqwatch-devices/routers/status?${queryParams.toString()}`
  );
  return response.data;
};

// 获取路由器历史数据
export const getRouterHistory = async (
  serial: string,
  params: RouterHistoryParams
): Promise<RouterHistoryDataPoint[]> => {
  const queryParams = new URLSearchParams({
    duration: params.duration,
    metric: params.metric,
  });

  const response = await api.get(
    `/iqwatch-devices/routers/${serial}/history?${queryParams.toString()}`
  );
  return response.data;
};
